/**
 * `Sidebar` — left-rail categorized navigation (M3 Phase A).
 *
 * Desktop-only chrome: hidden below Tailwind `md` (768px), where the
 * `<MobileNav>` (top bar + drawer + bottom tabs) takes over. The nav
 * registry lives in `navSections.tsx` and is shared with MobileNav.
 *
 * Behavior:
 *   - Always-visible expanded width (`w-60`) at ≥768px.
 *   - Collapses to icon-only (`w-14`) via the chevron button at the top.
 *   - Collapse state is persisted to `localStorage` under
 *     `reefapp.sidebarCollapsed` so refreshes/re-mounts remember it.
 *   - Active route gets a coral-accent fill (Abyss accent token) + accent left border.
 *   - `<LowReservoirBanner>` is rendered above the page content by
 *     `AppShell`, not inside the sidebar — it should be visible across
 *     every route.
 */
import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';

import { IconChevronLeft, IconChevronRight, NAV_SECTIONS } from './navSections';

const STORAGE_KEY = 'reefapp.sidebarCollapsed';

/* -------------------- Component ------------------------------------- */
export function Sidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  // Persist collapse state.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Storage failures are non-fatal — just don't persist.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((v) => !v), []);

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={clsx(
        'hidden md:flex flex-col border-r border-surface-stroke bg-bg-elevated backdrop-blur-md sticky top-0 h-screen z-30 shrink-0 transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      {/* Header: brand + collapse toggle */}
      <div
        className={clsx(
          'flex items-center border-b border-surface-stroke px-3 h-14',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {!collapsed ? (
          <span className="text-sm font-semibold tracking-tight text-text-1">
            <span className="text-accent">Reef</span>App
          </span>
        ) : null}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="sidebar-toggle"
          className="rounded p-1.5 text-text-2 hover:text-text-1 hover:bg-surface-glass transition"
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      </div>

      {/* Nav list — scrollable */}
      <nav
        className="flex-1 overflow-y-auto overflow-x-hidden py-2"
        data-testid="sidebar-nav"
      >
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} className="mb-2">
            {!collapsed ? (
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-3">
                {section.title}
              </div>
            ) : (
              <div
                aria-hidden
                className="mx-3 my-2 h-px bg-surface-stroke"
                data-section={section.title}
              />
            )}
            <ul className="flex flex-col gap-0.5 px-2">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    title={collapsed ? item.label : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                      clsx(
                        'group relative flex items-center rounded-md text-sm transition-colors',
                        collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-2.5 py-2',
                        isActive
                          ? 'bg-accent-soft text-accent'
                          : 'text-text-2 hover:bg-surface-glass hover:text-text-1',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Amber left accent bar on active */}
                        <span
                          aria-hidden
                          className={clsx(
                            'absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-sm transition-opacity',
                            isActive ? 'bg-accent opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="shrink-0">{item.icon}</span>
                        {!collapsed ? (
                          <>
                            <span className="truncate">{item.label}</span>
                            {item.badge != null ? (
                              <span className="ml-auto rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                                {item.badge}
                              </span>
                            ) : null}
                          </>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer note — version stamp lives in Settings, sidebar just shows brand mark */}
      {!collapsed ? (
        <div className="border-t border-surface-stroke px-3 py-2 text-[10px] text-text-3">
          v0.26.0 · M3
        </div>
      ) : null}
    </aside>
  );
}
